import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import SEOHead from "@/components/SEOHead";
import { submitContact } from "@/lib/api";
import { track } from "@/lib/analytics";

const PROJECT_TYPES = [
 "Custom Home",
 "Remodel / Renovation",
 "Addition",
 "Residential Construction",
 "Project Management",
 "Detail Carpentry / Finishes",
 "Something Else",
];

export default function Contact() {
 const [form, setForm] = useState({
 name: "",
 phone: "",
 email: "",
 project_type: "",
 message: "",
 website: "", // honeypot
 });
 const [status, setStatus] = useState("idle"); // idle | sending | success | error
 const [errors, setErrors] = useState({});

 const validate = () => {
 const e = {};
 if (!form.name.trim() || form.name.trim().length < 2) e.name = "Please enter your name.";
 if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) e.phone = "Please enter a valid phone number.";
 if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
 if (!form.project_type) e.project_type = "Please choose a project type.";
 if (!form.message.trim() || form.message.trim().length < 10) e.message = "A short message (10+ characters) helps us prepare.";
 setErrors(e);
 return Object.keys(e).length === 0;
 };

 const onChange = (k) => (ev) => {
 setForm({ ...form, [k]: ev.target.value });
 if (errors[k]) setErrors({ ...errors, [k]: undefined });
 };

 const onSubmit = async (ev) => {
 ev.preventDefault();
 if (!validate()) {
 toast.error("Please fix the highlighted fields.");
 return;
 }
 setStatus("sending");
 try {
 await submitContact(form);
 setStatus("success");
 track.contactSubmit(form.project_type);
 toast.success("Thanks, we'll be in touch within one business day.");
 setForm({ name: "", phone: "", email: "", project_type: "", message: "", website: "" });
 } catch (err) {
 setStatus("error");
 const statusCode = err?.response?.status;
 const detail = err?.response?.data?.detail;
 track.contactError(detail || err?.message || "unknown");
 if (statusCode === 429) {
 toast.error(detail || "Too many submissions. Please try again later.");
 } else {
 toast.error("Something went wrong. Please call (918) 373-4341 or try again.");
 }
 };
 };

 return (
 <div data-testid="contact-page">
 <SEOHead
 title="Contact Harris Homes | Tahlequah Home Builder"
 description="Call 918-373-4341 or send your lot details. Harris Homes will tell you what fits your land and budget in Tahlequah, OK."
 canonical="https://harrishomesok.com/contact"
 />
 {/* Header */}
 <section className="pt-20 md:pt-32 pb-12 md:pb-16 px-6 md:px-10">
 <div className="max-w-[1480px] mx-auto">
 <Reveal>
 <div className="overline mb-6">Contact</div>
 <h1 className="font-display text-5xl sm:text-7xl md:text-[7.5rem] leading-[0.95] tracking-tight max-w-5xl">
 Tell us about
 <br />
 <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
 your project.
 </span>
 </h1>
 <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
 Whether you've got blueprints in hand or just a few ideas
 sketched on a napkin, we'd love to hear about it. Send a note
 below or give us a call.
 </p>
 </Reveal>
 </div>
 </section>

 {/* Body */}
 <section className="px-6 md:px-10 pb-24 md:pb-32">
 <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
 {/* Contact info */}
 <Reveal className="md:col-span-4">
 <div className="overline mb-6">Direct</div>
 <div className="mb-8" data-testid="contact-info-nap">
 <div className="font-display text-xl">Harris Homes LLC</div>
 <div className="text-xs mt-1 tracking-wide" style={{ color: "var(--hh-text-dim)" }}>
 918-373-4341 · Tahlequah, OK
 </div>
 </div>
 <ul className="space-y-7">
 <li className="flex items-start gap-4">
 <Phone size={18} strokeWidth={1.3} style={{ color: "var(--hh-gold)", marginTop: 4 }} />
 <div>
 <div className="overline mb-1">Phone</div>
 <a data-testid="contact-info-phone" href="tel:+19183734341" onClick={() => track.phoneClick("contact-info")} className="font-display text-2xl md:text-3xl hover:text-[var(--hh-gold-light)] transition-colors">
 (918) 373-4341
 </a>
 </div>
 </li>
 <li className="flex items-start gap-4">
 <Mail size={18} strokeWidth={1.3} style={{ color: "var(--hh-gold)", marginTop: 4 }} />
 <div>
 <div className="overline mb-1">Email</div>
 <a data-testid="contact-info-email" href="mailto:4harrishomes@gmail.com" onClick={() => track.emailClick("contact-info")} className="font-display text-xl md:text-2xl break-all hover:text-[var(--hh-gold-light)] transition-colors">
 4harrishomes@gmail.com
 </a>
 </div>
 </li>
 <li className="flex items-start gap-4">
 <MapPin size={18} strokeWidth={1.3} style={{ color: "var(--hh-gold)", marginTop: 4 }} />
 <div>
 <div className="overline mb-1">Based In</div>
 <div data-testid="contact-info-location" className="font-display text-2xl">Tahlequah, Oklahoma</div>
 <div className="text-xs mt-1" style={{ color: "var(--hh-text-dim)" }}>
 Serving northeast OK
 </div>
 </div>
 </li>
 <li className="flex items-start gap-4">
 <Facebook size={18} strokeWidth={1.3} style={{ color: "var(--hh-gold)", marginTop: 4 }} />
 <div>
 <div className="overline mb-1">Social</div>
 <a
 data-testid="contact-info-facebook"
 href="https://www.facebook.com/p/Harris-Homes-61571695310257/"
 target="_blank"
 rel="noopener noreferrer"
 onClick={() => track.facebookClick("contact-info")}
 className="font-display text-xl hover:text-[var(--hh-gold-light)] transition-colors"
 >
 Harris Homes on Facebook
 </a>
 </div>
 </li>
 </ul>
 </Reveal>

 {/* Form */}
 <Reveal delay={0.1} className="md:col-span-8">
 {status === "success" ? (
 <div
 data-testid="contact-success"
 className="p-10 md:p-14 border text-center"
 style={{ borderColor: "var(--hh-gold)", background: "var(--hh-bg-2)" }}
 >
 <CheckCircle2 size={42} strokeWidth={1} style={{ color: "var(--hh-gold)" }} className="mx-auto mb-6" />
 <h2 className="font-display text-3xl md:text-4xl mb-4">Thank you.</h2>
 <p className="text-base md:text-lg leading-relaxed max-w-lg mx-auto" style={{ color: "var(--hh-text-dim)" }}>
 Your message is in. We'll be in touch within one business
 day, usually sooner.
 </p>
 <button
 type="button"
 onClick={() => setStatus("idle")}
 data-testid="contact-success-reset"
 className="btn-ghost mt-10"
 >
 Send Another
 </button>
 </div>
 ) : (
 <form
 data-testid="contact-form"
 onSubmit={onSubmit}
 noValidate
 className="space-y-6"
 >
 {/* Honeypot, hidden visually & from accessible tree */}
 <div className="absolute -left-[9999px] w-0 h-0 overflow-hidden" aria-hidden="true">
 <label>Website</label>
 <input
 type="text"
 tabIndex={-1}
 autoComplete="off"
 value={form.website}
 onChange={onChange("website")}
 />
 </div>

 <div className="grid sm:grid-cols-2 gap-6">
 <div>
 <label className="hh-label" htmlFor="cf-name">Name</label>
 <input
 id="cf-name"
 data-testid="contact-input-name"
 type="text"
 className="hh-input"
 value={form.name}
 onChange={onChange("name")}
 placeholder="Your full name"
 aria-invalid={!!errors.name}
 />
 {errors.name && (
 <p data-testid="contact-error-name" className="mt-2 text-xs flex items-center gap-2" style={{ color: "#d97757" }}>
 <AlertCircle size={12} /> {errors.name}
 </p>
 )}
 </div>
 <div>
 <label className="hh-label" htmlFor="cf-phone">Phone</label>
 <input
 id="cf-phone"
 data-testid="contact-input-phone"
 type="tel"
 className="hh-input"
 value={form.phone}
 onChange={onChange("phone")}
 placeholder="(918) 555-0000"
 aria-invalid={!!errors.phone}
 />
 {errors.phone && (
 <p data-testid="contact-error-phone" className="mt-2 text-xs flex items-center gap-2" style={{ color: "#d97757" }}>
 <AlertCircle size={12} /> {errors.phone}
 </p>
 )}
 </div>
 </div>

 <div className="grid sm:grid-cols-2 gap-6">
 <div>
 <label className="hh-label" htmlFor="cf-email">Email</label>
 <input
 id="cf-email"
 data-testid="contact-input-email"
 type="email"
 className="hh-input"
 value={form.email}
 onChange={onChange("email")}
 placeholder="you@example.com"
 aria-invalid={!!errors.email}
 />
 {errors.email && (
 <p data-testid="contact-error-email" className="mt-2 text-xs flex items-center gap-2" style={{ color: "#d97757" }}>
 <AlertCircle size={12} /> {errors.email}
 </p>
 )}
 </div>
 <div>
 <label className="hh-label" htmlFor="cf-type">Project Type</label>
 <select
 id="cf-type"
 data-testid="contact-input-project-type"
 className="hh-select"
 value={form.project_type}
 onChange={onChange("project_type")}
 aria-invalid={!!errors.project_type}
 >
 <option value="">Select a project type</option>
 {PROJECT_TYPES.map((t) => (
 <option key={t} value={t}>{t}</option>
 ))}
 </select>
 {errors.project_type && (
 <p data-testid="contact-error-project-type" className="mt-2 text-xs flex items-center gap-2" style={{ color: "#d97757" }}>
 <AlertCircle size={12} /> {errors.project_type}
 </p>
 )}
 </div>
 </div>

 <div>
 <label className="hh-label" htmlFor="cf-message">Tell us about your project</label>
 <textarea
 id="cf-message"
 data-testid="contact-input-message"
 rows={6}
 className="hh-textarea"
 value={form.message}
 onChange={onChange("message")}
 placeholder="Lot details, square footage, timeline, must-haves, dreams, anything that helps us prepare for our call…"
 aria-invalid={!!errors.message}
 />
 {errors.message && (
 <p data-testid="contact-error-message" className="mt-2 text-xs flex items-center gap-2" style={{ color: "#d97757" }}>
 <AlertCircle size={12} /> {errors.message}
 </p>
 )}
 </div>

 <div className="pt-2 flex flex-wrap items-center gap-4">
 <button
 type="submit"
 data-testid="contact-submit"
 className="btn-gold"
 disabled={status === "sending"}
 >
 {status === "sending" ? "Sending…" : "Send Message"}
 <Send size={14} strokeWidth={1.6} />
 </button>
 <span className="text-xs" style={{ color: "var(--hh-text-dim)" }}>
 We typically respond within one business day. By submitting, you agree to our{" "}
 <Link
 to="/privacy"
 data-testid="contact-privacy-link"
 className="underline underline-offset-4"
 style={{ color: "var(--hh-gold)" }}
 >
 privacy policy
 </Link>
 .
 </span>
 </div>

 {status === "error" && (
 <div
 data-testid="contact-error-banner"
 className="p-4 border text-sm flex items-start gap-3"
 style={{ borderColor: "#5a2a1f", background: "rgba(120,40,30,0.15)", color: "#f0c1ad" }}
 >
 <AlertCircle size={16} className="mt-0.5" />
 <span>
 Sorry, we couldn't submit your message. Please try
 again or call <strong>(918) 373-4341</strong>.
 </span>
 </div>
 )}
 </form>
 )}
 </Reveal>
 </div>
 </section>
 </div>
 );
}
